import {
  Popover,
  TextInput,
  Button,
  CloseButton,
  SegmentedControl,
  Textarea,
  Select,
  JsonInput,
  Badge,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useState } from 'react';
import type { FetchRequestPart } from '../converters';
import { InlineCodeHighlight } from '@mantine/code-highlight';
import { z } from 'zod';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { HttpMethod } from '@prisma/client';
import { zHttpHeaderKey } from '~/common/http/validation';
import { IconQuestionMark } from '@tabler/icons-react';

const ContentType = ['text/plain', 'application/json'] as const;
const METHODS_WITH_BODY = ['POST' satisfies HttpMethod, 'PUT' satisfies HttpMethod, 'PATCH' satisfies HttpMethod];

const zFetchRequest = z.object({
  url: z.string().min(1, 'URL is required').url('Please enter a valid URL'),
  method: z.enum(HttpMethod),
  headers: z.array(
    z.object({
      key: zHttpHeaderKey,
      value: z.string(),
    }),
  ),
  contentType: z.enum(ContentType),
  body: z.string(),
  responseVariableName: z.string(),
});

export default function FetchRequestPart({
  part: { url, method, headers, body, responseVariableName },
  onClick,
  toggleEditing,
  readonly,
  updateFunc,
}: {
  part: FetchRequestPart;
  onClick?: () => void;
  toggleEditing: (editing: boolean) => void;
  readonly?: boolean;
  updateFunc: (part: Omit<FetchRequestPart, 'type'>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);

  const form = useForm<z.infer<typeof zFetchRequest>>({
    initialValues: {
      url: url || '',
      method: method || HttpMethod.GET,
      headers: Object.entries(headers).map(([key, value]) => ({ key, value })),
      contentType: 'text/plain',
      body: body || '',
      responseVariableName: responseVariableName || '',
    },
    validate: zod4Resolver(zFetchRequest),
  });

  const handleClosePopover = useCallback(() => {
    toggleEditing(false);
    setEditing(false);
  }, [toggleEditing, setEditing]);

  const addHeader = () => {
    const currentHeaders = form.values.headers;
    form.setFieldValue('headers', [...currentHeaders, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    const currentHeaders = form.values.headers;

    form.setFieldValue(
      'headers',
      currentHeaders.filter((_, i) => i !== index),
    );
  };

  const handleSubmit = form.onSubmit(async (values) => {
    // Add content-type header if body is present
    const hasBody = METHODS_WITH_BODY.includes(values.method);
    const headers = hasBody ? [...values.headers, { key: 'Content-Type', value: values.contentType }] : values.headers;
    const parsedHeaders = Object.fromEntries(headers.map(({ key, value }) => [key.trim(), value.trim()]));

    handleClosePopover();

    await updateFunc({
      url: values.url,
      method: values.method,
      headers: parsedHeaders,
      body: values.body,
      responseVariableName: values.responseVariableName,
    });
  });

  return (
    <div className="flex items-center space-x-1 px-1">
      <Popover
        width={365}
        position="right"
        withArrow
        shadow="md"
        opened={editing}
        closeOnClickOutside
        onClose={handleClosePopover}
        onDismiss={handleClosePopover}
      >
        <Popover.Target>
          <div
            className="break rounded-md"
            onDoubleClick={() => {
              onClick?.();
              toggleEditing(true);
              setEditing(true);
            }}
          >
            <MethodBadge method={method} />{' '}
            <span className="break-all text-yellow-900 hover:cursor-pointer dark:text-yellow-600">
              {url || 'No URL specified'}
            </span>
            {!!responseVariableName && (
              <span className="break-words">
                {' '}
                stored as{' '}
                <InlineCodeHighlight
                  code={`VARS.${responseVariableName}`}
                  language="tsx"
                />
              </span>
            )}
          </div>
        </Popover.Target>
        <Popover.Dropdown className="dark:bg-neutral-800">
          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <div className="text-sm">Fetch Request</div>
              <CloseButton
                variant="transparent"
                size="md"
                onClick={handleClosePopover}
              />
            </div>
            <form
              className="mb-2 flex flex-col gap-2"
              onSubmit={handleSubmit}
            >
              <SegmentedControl
                className="mt-2"
                data={Object.values(HttpMethod).map((method) => ({ label: method, value: method }))}
                disabled={readonly}
                fullWidth
                {...form.getInputProps('method')}
              />
              <div className="mt-2 flex flex-col gap-4">
                <TextInput
                  label="URL"
                  disabled={readonly}
                  placeholder="https://example.com/api"
                  {...form.getInputProps('url')}
                />

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-[13px] font-medium">Headers</label>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      onClick={addHeader}
                      disabled={readonly}
                    >
                      Add Header
                    </Button>
                  </div>
                  {form.values.headers.map((_, index) => (
                    <div
                      key={index}
                      className="mb-2 flex items-baseline gap-2"
                    >
                      <TextInput
                        placeholder="Header name"
                        className="flex-1"
                        disabled={readonly}
                        {...form.getInputProps(`headers.${index}.key`)}
                      />
                      <TextInput
                        placeholder="Header value"
                        className="flex-1"
                        disabled={readonly}
                        {...form.getInputProps(`headers.${index}.value`)}
                      />

                      <CloseButton
                        size="sm"
                        onClick={() => removeHeader(index)}
                        disabled={readonly}
                      />
                    </div>
                  ))}
                </div>

                {METHODS_WITH_BODY.includes(form.values.method) && (
                  <>
                    <Select
                      // withinPortal needed to prevent content type dropdown selection from closing the main Popover
                      comboboxProps={{ withinPortal: false }}
                      description="The content type of the request body"
                      label="Content Type"
                      disabled={readonly}
                      allowDeselect={false}
                      data={ContentType.map((contentType) => ({ label: contentType, value: contentType }))}
                      {...form.getInputProps('contentType')}
                    />
                    {form.values.contentType === 'text/plain' ?
                      <Textarea
                        label="Text Body"
                        description="Text to be sent as the body of the request"
                        disabled={readonly}
                        autosize
                        minRows={2}
                        maxRows={4}
                        {...form.getInputProps('body')}
                      />
                    : form.values.contentType === 'application/json' ?
                      <JsonInput
                        label="JSON Body"
                        description="JSON to be sent as the body of the request"
                        validationError="Invalid JSON"
                        disabled={readonly}
                        autosize
                        minRows={2}
                        maxRows={4}
                        {...form.getInputProps('body')}
                      />
                    : null}
                  </>
                )}

                <div className="flex items-center gap-2">
                  <TextInput
                    label={
                      <div className="flex items-center gap-1">
                        Response Variable Name (optional)
                        <Tooltip
                          label={
                            <pre className="text-xs">
                              {JSON.stringify(
                                {
                                  status: 'number',
                                  body: 'string',
                                  bodyAsJson: 'any',
                                },
                                null,
                                2,
                              )}
                            </pre>
                          }
                          multiline
                          position="right"
                          withArrow
                        >
                          <div className="mb-1 self-end">
                            <IconQuestionMark
                              className="translate-y-px text-[var(--mantine-color-blue-7)] dark:text-[var(--mantine-color-blue-3)]"
                              size={16}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    }
                    description="The fetch result will be stored in this variable"
                    disabled={readonly}
                    placeholder="response"
                    className="flex-1"
                    {...form.getInputProps('responseVariableName')}
                  />
                </div>
              </div>

              {!readonly && (
                <div className="flex justify-end space-x-1 py-4">
                  <Button
                    type="submit"
                    disabled={readonly}
                  >
                    Save
                  </Button>
                </div>
              )}
            </form>
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}

function MethodBadge({ method }: { method: HttpMethod }) {
  return (
    <Badge
      size="sm"
      radius="sm"
      className="-translate-y-px"
      color={
        method === HttpMethod.GET ? 'green'
        : method === HttpMethod.POST ?
          'yellow'
        : method === HttpMethod.PUT ?
          'blue'
        : method === HttpMethod.DELETE ?
          'red'
        : method === HttpMethod.PATCH ?
          'pink'
        : 'gray'
      }
      variant="light"
    >
      {method}
    </Badge>
  );
}
